/**
 * services/EmailCategoryService.ts
 * Email category business logic and orchestration
 *
 * Handles email category management for organizing email templates
 * across different application areas (Beta Applications, User Management, etc.)
 *
 * Architecture: WU1.5 Task 8 - Repository Pattern
 * - All database access through emailCategoriesRepository
 * - Service layer handles business logic, validation, orchestration
 * - No direct Supabase access
 */

import { emailCategoriesRepository } from '@/repositories/emailCategoriesRepository';
import type {
  EmailCategory,
  EmailCategoryInsert,
  EmailCategoryUpdate
} from '@/types/supabase/EmailCategory';

/**
 * Email Category Service
 *
 * Business logic layer for email category management.
 * Orchestrates category operations, validation, and seeding.
 * All database access delegated to emailCategoriesRepository.
 */
export class EmailCategoryService {
  // ===== CORE CRUD METHODS =====

  /**
   * Get category by ID
   *
   * @param id - Category ID
   * @returns Promise<EmailCategory | null> - Category if found, null otherwise
   */
  async getById(id: string): Promise<EmailCategory | null> {
    try {
      return await emailCategoriesRepository.getById(id);
    } catch (error) {
      console.error(`Failed to get category ${id}:`, error);
      throw new Error('Failed to retrieve email category');
    }
  }

  /**
   * Get category by name
   *
   * Primary lookup method for category access.
   *
   * @param name - Category name (e.g., "Beta Applications", "User Management")
   * @returns Promise<EmailCategory | null> - Category if found, null otherwise
   */
  async getByName(name: string): Promise<EmailCategory | null> {
    try {
      return await emailCategoriesRepository.getByName(name);
    } catch (error) {
      console.error(`Failed to get category by name ${name}:`, error);
      throw new Error('Failed to retrieve email category');
    }
  }

  /**
   * List all categories
   *
   * @returns Promise<EmailCategory[]> - All categories ordered by name
   */
  async list(): Promise<EmailCategory[]> {
    try {
      return await emailCategoriesRepository.list();
    } catch (error) {
      console.error('Failed to list categories:', error);
      throw new Error('Failed to retrieve email categories');
    }
  }

  /**
   * Create new category
   *
   * Validates that name is unique before creating.
   *
   * @param categoryData - Category data
   * @returns Promise<EmailCategory> - Created category
   * @throws Error if category with same name already exists
   */
  async create(categoryData: EmailCategoryInsert): Promise<EmailCategory> {
    try {
      // Check for duplicate name
      const existing = await emailCategoriesRepository.getByName(categoryData.name);
      if (existing) {
        throw new Error(`Category with name "${categoryData.name}" already exists`);
      }

      return await emailCategoriesRepository.create(categoryData);
    } catch (error) {
      console.error('Failed to create category:', error);
      throw error; // Re-throw to preserve specific error messages
    }
  }

  /**
   * Update category
   *
   * If name is being updated, validates uniqueness.
   *
   * @param id - Category ID
   * @param updates - Category updates
   * @returns Promise<EmailCategory> - Updated category
   * @throws Error if new name conflicts with existing category
   */
  async update(id: string, updates: EmailCategoryUpdate): Promise<EmailCategory> {
    try {
      // If updating name, check for duplicates
      if (updates.name) {
        const existing = await emailCategoriesRepository.getByName(updates.name);
        if (existing && existing.id !== id) {
          throw new Error(`Category with name "${updates.name}" already exists`);
        }
      }

      return await emailCategoriesRepository.update(id, updates);
    } catch (error) {
      console.error(`Failed to update category ${id}:`, error);
      throw error; // Re-throw to preserve specific error messages
    }
  }

  /**
   * Delete category
   *
   * WARNING: Will fail if templates reference this category (FK constraint).
   *
   * @param id - Category ID
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    try {
      await emailCategoriesRepository.delete(id);
    } catch (error) {
      console.error(`Failed to delete category ${id}:`, error);
      throw new Error('Failed to delete email category. Ensure no templates reference this category.');
    }
  }

  // ===== SEEDING AND HELPERS =====

  /**
   * Ensure category exists, create if missing
   *
   * Idempotent helper for seeding default categories.
   * Returns existing category if found, creates new one if not.
   *
   * @param name - Category name
   * @param description - Category description
   * @returns Promise<EmailCategory> - Existing or newly created category
   */
  async ensureCategoryExists(name: string, description: string): Promise<EmailCategory> {
    try {
      // Check if category already exists
      const existing = await emailCategoriesRepository.getByName(name);
      if (existing) {
        console.log(`✅ Category "${name}" already exists`);
        return existing;
      }

      // Create new category
      console.log(`🌱 Creating category "${name}"`);
      const newCategory = await emailCategoriesRepository.create({
        name,
        description
      });

      return newCategory;
    } catch (error) {
      console.error(`Failed to ensure category exists (${name}):`, error);
      throw new Error(`Failed to ensure "${name}" category exists`);
    }
  }

  /**
   * Seed default categories
   *
   * Creates default categories if they don't exist:
   * - Beta Applications
   * - User Management
   *
   * Idempotent - safe to call multiple times.
   *
   * @returns Promise<void>
   */
  async seedDefaultCategories(): Promise<void> {
    try {
      console.log('🌱 Seeding default email categories...');

      await this.ensureCategoryExists(
        'Beta Applications',
        'Email templates for beta application workflow'
      );

      await this.ensureCategoryExists(
        'User Management',
        'User invitation, activation, and account management emails'
      );

      console.log('✅ Default email categories seeded');
    } catch (error) {
      console.error('Failed to seed default categories:', error);
      throw new Error('Failed to seed default email categories');
    }
  }
}

// Export singleton instance
export const emailCategoryService = new EmailCategoryService();
